// Constructeur de formulaire : palette des types à gauche, sections et champs au centre,
// propriétés du champ sélectionné à droite. Glisser-déposer natif (palette vers une section,
// champ vers une autre position) et équivalents clavier : Entrée sur un type ajoute le champ dans
// la section courante, Alt + flèches déplace le champ qui a le focus, flèches parcourent la liste.
import { LIBELLES_TYPES_CHAMP, tousLesChamps, type Formulaire, type TypeChamp } from '@cc/contrats';
import { Bouton, Champ as ChampTexte, ConfirmationIntegree, ZoneTexte } from '@cc/ui';
import { ArrowDown, ArrowUp, GripVertical, Plus } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import type { TypePiece } from '../contenu';
import {
  ajouterChamp,
  ajouterSection,
  deplacerChamp,
  deplacerChampDunCran,
  deplacerSection,
  estTypeChamp,
  remplacerChamp,
  remplacerSection,
  supprimerChamp,
  supprimerSection,
  TYPES_PALETTE,
  type PositionChamp,
} from './constructeur';
import { EditeurChamp } from './EditeurChamp';
import { EditeurCondition, texteCondition } from './EditeurCondition';
import demarches from '../demarches.module.css';
import styles from './constructeur.module.css';

export interface ProprietesConstructeur {
  formulaire: Formulaire;
  onChanger: (f: Formulaire) => void;
  typesPieces: TypePiece[];
  rubriqueTarif: string | null;
  lectureSeule?: boolean;
}

const TYPE_PALETTE = 'application/x-cc-type-champ';
const TYPE_POSITION = 'application/x-cc-position';

export function ConstructeurFormulaire({ formulaire, onChanger, typesPieces, rubriqueTarif, lectureSeule }: ProprietesConstructeur) {
  const [selection, setSelection] = useState<PositionChamp | null>(null);
  const [sectionCourante, setSectionCourante] = useState(0);
  const [cibleSurvol, setCibleSurvol] = useState<string | null>(null);
  const aFocaliser = useRef<string | null>(null);
  const champs = tousLesChamps(formulaire).map((x) => x.champ);
  const codesPris = champs.map((c) => c.code);
  const champSelectionne = selection ? formulaire.sections[selection.section]?.champs[selection.champ] : undefined;

  useEffect(() => {
    if (aFocaliser.current) {
      document.getElementById(`champ-item-${aFocaliser.current}`)?.focus();
      aFocaliser.current = null;
    }
  });

  const appliquer = (r: { formulaire: Formulaire; position: PositionChamp }) => {
    onChanger(r.formulaire);
    setSelection(r.position);
    setSectionCourante(r.position.section);
    aFocaliser.current = r.formulaire.sections[r.position.section]?.champs[r.position.champ]?.code ?? null;
  };

  const ajouter = (type: TypeChamp, section?: number, index?: number) => appliquer(ajouterChamp(formulaire, type, section ?? sectionCourante, index));

  const deposer = (e: DragEvent, section: number, index: number) => {
    e.preventDefault();
    setCibleSurvol(null);
    if (lectureSeule) return;
    const type = e.dataTransfer.getData(TYPE_PALETTE);
    if (type && estTypeChamp(type)) return ajouter(type, section, index);
    const brut = e.dataTransfer.getData(TYPE_POSITION);
    if (!brut) return;
    const de = JSON.parse(brut) as PositionChamp;
    appliquer(deplacerChamp(formulaire, de, { section, champ: index }));
  };

  const survoler = (e: DragEvent, cle: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (cibleSurvol !== cle) setCibleSurvol(cle);
  };

  const clavierChamp = (e: KeyboardEvent, p: PositionChamp) => {
    if (lectureSeule) return;
    if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      appliquer(deplacerChampDunCran(formulaire, p, e.key === 'ArrowUp' ? -1 : 1));
      return;
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const liste = tousLesChamps(formulaire);
      const i = liste.findIndex((x) => x.champ.code === formulaire.sections[p.section]?.champs[p.champ]?.code);
      const suivant = liste[i + (e.key === 'ArrowDown' ? 1 : -1)];
      if (suivant) document.getElementById(`champ-item-${suivant.champ.code}`)?.focus();
    }
  };

  return (
    <div className={styles.constructeur}>
      <aside className={styles.palette} aria-labelledby="palette-titre">
        <h3 id="palette-titre" className="cc-etiquette">
          Champs disponibles
        </h3>
        <ul className={styles.listePalette}>
          {TYPES_PALETTE.map(({ type, symbole }) => (
            <li key={type}>
              <button
                type="button"
                className={styles.boutonPalette}
                draggable={!lectureSeule}
                disabled={lectureSeule}
                onDragStart={(e) => {
                  e.dataTransfer.setData(TYPE_PALETTE, type);
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => ajouter(type)}
                aria-label={`Ajouter un champ ${LIBELLES_TYPES_CHAMP[type]} à la section ${sectionCourante + 1}`}
              >
                <span className={styles.symbole} aria-hidden>
                  {symbole}
                </span>
                <span>{LIBELLES_TYPES_CHAMP[type]}</span>
                <Plus aria-hidden width={14} height={14} />
              </button>
            </li>
          ))}
        </ul>
        <p className={demarches.aideDiscrete}>
          Glisser-déposer, ou au clavier : <kbd>Entrée</kbd> ajoute le champ à la section courante, <kbd>Alt</kbd> + flèches déplacent le champ qui a le focus, flèches haut et bas parcourent les champs.
        </p>
      </aside>

      <div className={styles.zoneSections}>
        {formulaire.sections.length === 0 ? <p className={demarches.aideDiscrete}>Aucune section : ajoutez un champ depuis la palette (une section « Informations » sera créée) ou créez une section.</p> : null}
        {formulaire.sections.map((s, si) => (
          <section key={s.code} className={styles.sectionConstructeur} aria-labelledby={`titre-section-${s.code}`} data-courante={si === sectionCourante || undefined} onFocus={() => setSectionCourante(si)}>
            <div className={styles.enteteSection}>
              <h3 id={`titre-section-${s.code}`}>
                Section {si + 1} : {s.titre || 'sans titre'} <span className={styles.compteur}>{s.champs.length} champ{s.champs.length > 1 ? 's' : ''}</span>
              </h3>
              {!lectureSeule ? (
                <div className={demarches.actionsSection}>
                  <Bouton petit variante="discret" icone={ArrowUp} aria-label={`Monter la section ${si + 1}`} disabled={si === 0} onClick={() => onChanger(deplacerSection(formulaire, si, -1))}>
                    Monter
                  </Bouton>
                  <Bouton petit variante="discret" icone={ArrowDown} aria-label={`Descendre la section ${si + 1}`} disabled={si === formulaire.sections.length - 1} onClick={() => onChanger(deplacerSection(formulaire, si, 1))}>
                    Descendre
                  </Bouton>
                  <ConfirmationIntegree libelle="Retirer la section" petit variante="discret" question={`Retirer la section « ${s.titre} » et ses ${s.champs.length} champ(s) ?`} libelleConfirmer="Retirer la section" onConfirmer={() => { onChanger(supprimerSection(formulaire, si)); setSelection(null); }} />
                </div>
              ) : null}
            </div>
            {!lectureSeule ? (
              <details className={styles.detailsSection}>
                <summary>Titre, description et condition de la section</summary>
                <div className={demarches.pile}>
                  <ChampTexte libelle="Titre de la section" value={s.titre} required onChange={(e) => onChanger(remplacerSection(formulaire, si, { titre: e.target.value }))} />
                  <ZoneTexte libelle="Description" value={s.description ?? ''} facultatif rows={2} onChange={(e) => onChanger(remplacerSection(formulaire, si, { description: e.target.value || undefined }))} />
                  <EditeurCondition libelle="Condition d'affichage de la section" condition={s.visible_si} onChanger={(visible_si) => onChanger(remplacerSection(formulaire, si, { visible_si }))} champs={champs.filter((c) => !s.champs.some((x) => x.code === c.code))} />
                </div>
              </details>
            ) : null}
            <ol className={styles.listeChamps} aria-label={`Champs de la section ${s.titre}`}>
              {s.champs.map((c, ci) => {
                const p = { section: si, champ: ci };
                const choisi = selection?.section === si && selection.champ === ci;
                const cle = `${si}-${ci}`;
                return (
                  <li key={c.code} className={styles.itemChamp} data-survol={cibleSurvol === cle || undefined} onDragOver={(e) => survoler(e, cle)} onDragLeave={() => setCibleSurvol(null)} onDrop={(e) => deposer(e, si, ci)}>
                    <div
                      id={`champ-item-${c.code}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={choisi}
                      aria-label={`${c.libelle}, ${LIBELLES_TYPES_CHAMP[c.type]}${c.obligatoire ? ', obligatoire' : ''}${c.visible_si ? ', conditionnel' : ''}`}
                      className={styles.carteChamp}
                      data-choisi={choisi || undefined}
                      draggable={!lectureSeule}
                      onDragStart={(e) => {
                        e.dataTransfer.setData(TYPE_POSITION, JSON.stringify(p));
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onClick={() => { setSelection(p); setSectionCourante(si); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelection(p);
                          setSectionCourante(si);
                        } else clavierChamp(e, p);
                      }}
                    >
                      <GripVertical aria-hidden width={14} height={14} className={styles.poignee} />
                      <div className={styles.corpsChamp}>
                        <span className={styles.libelleChamp}>{c.libelle || c.code}</span>
                        <span className={styles.detailChamp}>
                          {c.visible_si ? `Visible si ${texteCondition(c.visible_si, champs)}` : c.aide ? `Aide : ${c.aide}` : c.options ? c.options.map((o) => o.libelle).join(', ') : c.calcul ? 'Calculé par le serveur' : ''}
                        </span>
                      </div>
                      <div className={styles.metaChamp}>
                        <span className="cc-mono">{LIBELLES_TYPES_CHAMP[c.type]}</span>
                        <span>{c.obligatoire ? 'Obligatoire' : 'Facultatif'}</span>
                      </div>
                    </div>
                  </li>
                );
              })}
              <li className={styles.zoneDepot} data-survol={cibleSurvol === `${si}-fin` || undefined} onDragOver={(e) => survoler(e, `${si}-fin`)} onDragLeave={() => setCibleSurvol(null)} onDrop={(e) => deposer(e, si, s.champs.length)}>
                Déposer un champ ici
              </li>
            </ol>
          </section>
        ))}
        {!lectureSeule ? (
          <div>
            <Bouton variante="secondaire" icone={Plus} onClick={() => onChanger(ajouterSection(formulaire))}>
              Ajouter une section
            </Bouton>
          </div>
        ) : null}
      </div>

      <aside className={styles.colonneProprietes} aria-label="Propriétés du champ sélectionné">
        {champSelectionne && selection && !lectureSeule ? (
          <EditeurChamp
            key={`${selection.section}-${selection.champ}`}
            champ={champSelectionne}
            champs={champs}
            typesPieces={typesPieces}
            rubriqueTarif={rubriqueTarif}
            codesPris={codesPris}
            onChanger={(c) => onChanger(remplacerChamp(formulaire, selection, c))}
            onSupprimer={() => {
              onChanger(supprimerChamp(formulaire, selection));
              setSelection(null);
            }}
          />
        ) : (
          <p className={demarches.aideDiscrete}>{lectureSeule ? 'Version en lecture seule : les propriétés ne sont pas modifiables.' : 'Sélectionnez un champ pour modifier ses propriétés.'}</p>
        )}
      </aside>
    </div>
  );
}
